/**
 * `useInfobarStore` — the chat infobar's message queue (miniplan §3.2).
 *
 * ONE line is visible at a time. Callers `post()` already-localised text and
 * the store owns everything after that: ordering, lifetime, coalescing and
 * the timers. The component only renders `current`.
 *
 * EPHEMERAL BY DESIGN — no `persist`, no `partialize`. A status ticker that
 * replayed yesterday's lines after a reload would be lying about liveness
 * (I-5), and a marker surviving a reload has no in-flight work behind it.
 *
 * ── The rules, and why each exists ──────────────────────────────────────
 *
 * • TTL IS CLAMPED to [1.5s, 15s]. No caller can park a non-sticky line
 *   forever or flash one too fast to read.
 *
 * • `sticky` REQUIRES a `key`. A sticky with no owner handle cannot be
 *   dismissed by anyone, so it is a leak by construction; the store refuses
 *   it (throws in DEV so it cannot ship, warns + drops in prod so it cannot
 *   take a user's turn down with it).
 *
 * • STICKY YIELDS, ALWAYS. A sticky is *ambient truth* ("processing…"); a
 *   ticker line is *news* ("mission failed"). Truth can wait, news cannot —
 *   so any non-sticky post preempts a sticky IMMEDIATELY (it is owed no
 *   min-display) and the sticky returns once the queue drains. Without this
 *   one rule a sticky `concierge:turn` would block every ticker line behind
 *   it for the whole turn.
 *
 * • MIN-DISPLAY (3s) before a non-sticky line may be preempted, so a burst
 *   cannot make an earlier message vanish before it was readable. This is
 *   what makes the bar a QUEUE rather than a last-write-wins slot: messages
 *   ROTATE, each getting its turn, instead of newer ones stomping older
 *   ones. A same-key update is exempt — that is the same line changing, not
 *   a new one arriving.
 *
 * • SAME-KEY COALESCING updates in place. A mission's progress replaces its
 *   own line instead of queueing behind it. The TTL restarts (the line is
 *   freshly true) but the DISPLAY CLOCK does not, so coalescing can never be
 *   used to starve the queue behind it.
 *
 * • `maxAgeMs` BACKSTOP on stickies. The dismiss seams for a "processing"
 *   sticky live in another component's lifecycle, and every one of them can
 *   be missed (navigation mid-turn, an unmount, a seam that never fires). A
 *   stuck sticky is not a cosmetic bug: it is a working-indicator that lies
 *   indefinitely on the most-used surface. The backstop makes the worst case
 *   "it disappeared early" instead — silence about an unknown is honest, a
 *   confident wrong state is not.
 *
 * Timers live HERE, not in the component: a `useEffect` keyed on a route or a
 * mount is exactly the trap that has bitten this lane before (an effect that
 * fires on mount clears state it did not create).
 */
import { create } from "zustand";

export type InfobarMode = "info" | "progress" | "success" | "error";
export type InfobarEffect = "fade" | "none";

/**
 * MIN_DISPLAY_MS — how long a line is guaranteed on screen before anything
 * may preempt it. 3s, operator 2026-07-30 (the plan said ~1.5s, which is
 * fine for a glance but not for READING a sentence in a second language at
 * 10px). The bar is worth nothing if a lawyer sees that something happened
 * but not what.
 *
 * TTL_MIN_MS is pinned TO the same value on purpose: a message whose TTL
 * were shorter than its guaranteed display would be a contradiction the
 * store could not honour — it would have to retire a line it just promised
 * to show. Keep these equal, or make TTL_MIN the larger of the two.
 */
export const MIN_DISPLAY_MS = 3_000;
export const TTL_MIN_MS = MIN_DISPLAY_MS;
export const TTL_MAX_MS = 15_000;
export const TTL_DEFAULT_MS = 5_000;
/** Hard ceiling on any sticky, however its owner's dismiss seams behave. */
export const STICKY_MAX_AGE_DEFAULT_MS = 120_000;

export interface InfobarMessage {
  id: string;
  /** Same-key posts REPLACE in place. REQUIRED for `sticky`. Session-scope
   *  it (`concierge:turn:${sessionKey}`, `mission:${missionId}`) — a bare
   *  global key lets two concurrent surfaces coalesce onto one line and lets
   *  the first to finish dismiss it for both. */
  key?: string;
  /** ALREADY-LOCALISED. Callers own i18n — the store never sees a key. */
  text: string;
  mode?: InfobarMode;
  ttlMs?: number;
  sticky?: boolean;
  effect?: InfobarEffect;
  /** Higher preempts FIFO. Never overrides min-display. */
  priority?: number;
  /** Sticky only: hard expiry. Defaults to STICKY_MAX_AGE_DEFAULT_MS. */
  maxAgeMs?: number;
  /** Epoch ms of the post — set by the store, used for the sticky backstop. */
  postedAt: number;
}

export type InfobarPost = Omit<InfobarMessage, "id" | "postedAt">;

interface InfobarStore {
  queue: InfobarMessage[];
  current: InfobarMessage | null;
  /** Epoch ms `current` began displaying — drives MIN_DISPLAY_MS. */
  currentSince: number | null;
  post: (msg: InfobarPost) => string;
  dismiss: (idOrKey: string) => void;
  clear: () => void;
}

export function clampTtl(ttlMs: number | undefined): number {
  const raw = ttlMs ?? TTL_DEFAULT_MS;
  return Math.min(Math.max(raw, TTL_MIN_MS), TTL_MAX_MS);
}

let seq = 0;
const mintId = () => `ib_${++seq}`;

// Module-level timer handles. Deliberately NOT in the store: they are not
// state, nothing renders from them, and putting them in would make every
// timer reschedule a store update.
let displayTimer: ReturnType<typeof setTimeout> | null = null;
const clearDisplayTimer = () => {
  if (displayTimer !== null) {
    clearTimeout(displayTimer);
    displayTimer = null;
  }
};

/**
 * Pick what should be on screen. Non-sticky ALWAYS beats sticky (the yield
 * rule); within a class, higher priority first, then FIFO by insertion.
 * `queue` is kept in insertion order, so a stable sort preserves FIFO.
 */
function selectNext(queue: InfobarMessage[]): InfobarMessage | null {
  if (queue.length === 0) return null;
  let best = queue[0]!;
  for (let i = 1; i < queue.length; i++) {
    const c = queue[i]!;
    const cSticky = c.sticky === true;
    const bSticky = best.sticky === true;
    // Strictly-greater comparisons keep this stable, i.e. FIFO within a class.
    if (cSticky !== bSticky) {
      if (bSticky) best = c;
      continue;
    }
    if ((c.priority ?? 0) > (best.priority ?? 0)) best = c;
  }
  return best;
}

export const useInfobarStore = create<InfobarStore>((set, get) => {
  /** Promote the best queued message into `current`, arming its lifetime. */
  function promote(now: number) {
    clearDisplayTimer();
    const { queue } = get();
    const next = selectNext(queue);
    if (!next) {
      set({ current: null, currentSince: null });
      return;
    }
    set({
      current: next,
      currentSince: now,
      queue: queue.filter((m) => m.id !== next.id),
    });
    armTimer(next, now);
  }

  /** A non-sticky message expires on its TTL; a sticky only on its backstop. */
  function armTimer(msg: InfobarMessage, now: number) {
    clearDisplayTimer();
    // ⚠ ABSOLUTE CEILING, measured from the FIRST post of a key.
    //
    // Coalescing re-arms a fresh TTL on every same-key update, so a chatty
    // sender could hold its line for as long as it kept talking: the mission
    // ticker posts every transition under `mission:<id>`, and a busy mission
    // emits for as long as it runs. Observed live at 20–30s against a 6s TTL.
    // The "TTL is clamped so no caller can park a message forever" promise was
    // therefore true of a single post and FALSE of a coalescing key — the
    // clamp bounded each refresh, not the line's life.
    //
    // `postedAt` survives coalescing precisely so this can be enforced, which
    // is why the sticky backstop already read it. Now every message gets one.
    const elapsed = now - msg.postedAt;
    const ceiling = msg.sticky
      ? (msg.maxAgeMs ?? STICKY_MAX_AGE_DEFAULT_MS)
      : (msg.maxAgeMs ?? TTL_MAX_MS);
    const remaining = Math.max(ceiling - elapsed, 0);
    const delay = msg.sticky ? remaining : Math.min(clampTtl(msg.ttlMs), remaining);
    displayTimer = setTimeout(() => {
      const { current } = get();
      // A sticky that hits its backstop is DROPPED, not re-queued — the
      // whole point is that its owner never dismissed it.
      if (current?.id === msg.id) promote(Date.now());
    }, delay);
  }

  return {
    queue: [],
    current: null,
    currentSince: null,

    post: (msg) => {
      const now = Date.now();

      if (msg.sticky && !msg.key) {
        const why =
          "[infobar] a sticky message requires a `key` — without one nothing can dismiss it. Refusing the post.";
        if (import.meta.env.DEV) throw new Error(why);
        // eslint-disable-next-line no-console
        console.warn(why, msg.text);
        return "";
      }

      const full: InfobarMessage = { ...msg, id: mintId(), postedAt: now };
      const { current, queue, currentSince } = get();

      // ── Coalesce: the same key already on screen is the SAME LINE changing.
      // Text/mode update in place; the TTL restarts (freshly true) but
      // `currentSince` does NOT, so a chatty key cannot starve the queue.
      if (msg.key && current?.key === msg.key) {
        const merged: InfobarMessage = { ...full, id: current.id, postedAt: current.postedAt };
        set({ current: merged });
        armTimer(merged, now);
        return merged.id;
      }

      // …or the same key waiting in the queue: replace it, keep its slot.
      if (msg.key && queue.some((m) => m.key === msg.key)) {
        set({
          queue: queue.map((m) =>
            m.key === msg.key ? { ...full, id: m.id, postedAt: m.postedAt } : m,
          ),
        });
        return full.id;
      }

      // ⚠ IDENTICAL TEXT DOES NOT REPLAY. Different keys saying the SAME
      // sentence is a real shape here, not a hypothetical: several probe
      // missions share one goal, so N missionIds produce N identical lines.
      // Queued back-to-back they read as ONE message stuck for N x TTL — half
      // of the ~30s the operator reported. The bar shows what is TRUE, and
      // "this already says that" is true; repeating it adds nothing and costs
      // the queue behind it its turn.
      //
      // Scoped to what is on screen or already waiting, so the same sentence
      // is welcome again once the bar has gone quiet — this suppresses an echo,
      // not a recurrence.
      if (current?.text === full.text || queue.some((m) => m.text === full.text)) {
        return current?.text === full.text ? current.id : full.id;
      }

      const nextQueue = [...queue, full];
      set({ queue: nextQueue });

      if (!current) {
        promote(now);
        return full.id;
      }

      // A sticky NEVER preempts anything.
      if (full.sticky) return full.id;

      // Non-sticky vs a sticky on screen: immediate preemption, no
      // min-display owed — the sticky goes back to the queue and returns
      // when the queue drains.
      if (current.sticky) {
        set({ queue: [...nextQueue, current] });
        promote(now);
        return full.id;
      }

      // Non-sticky vs non-sticky: respect min-display, then re-evaluate.
      const shown = now - (currentSince ?? now);
      if (shown >= MIN_DISPLAY_MS) {
        set({ queue: [...nextQueue, current] });
        promote(now);
      }
      // else: the armed TTL/min-display timer will promote it in turn.
      return full.id;
    },

    dismiss: (idOrKey) => {
      const { current, queue } = get();
      const nextQueue = queue.filter((m) => m.id !== idOrKey && m.key !== idOrKey);
      const hitsCurrent = current?.id === idOrKey || (!!current?.key && current.key === idOrKey);
      if (hitsCurrent) {
        set({ queue: nextQueue });
        promote(Date.now());
        return;
      }
      if (nextQueue.length !== queue.length) set({ queue: nextQueue });
    },

    clear: () => {
      clearDisplayTimer();
      set({ queue: [], current: null, currentSince: null });
    },
  };
});

/** Test-only reset — the store is module-level, so suites must isolate. */
export function __resetInfobarStoreForTests() {
  clearDisplayTimer();
  seq = 0;
  useInfobarStore.setState({ queue: [], current: null, currentSince: null });
}
